import { Button, Form } from "react-bootstrap";
import { URLSearchParamsInit } from "react-router-dom";
import { Certification } from "../Models/Certification";

interface IProps {
  certifications: Certification[];
  selectedCertificationIds: number[];
  includeAll: boolean;
  searchParams: URLSearchParams;
  setSearchParams: (nextInit: URLSearchParamsInit) => void;
  onCertificationChange: (certificationIds: number[]) => void;
  onIncludeAllChange: (includeAll: boolean) => void;
  onClearFilters: () => void;
  clearDisabled: boolean;
}

const activityTypeOptions = [
  { label: "CME", paramName: "CmeFilter" },
  { label: "SA-CME", paramName: "SaFilter" },
  { label: "PIP Clinical", paramName: "PipClinicalFilter" },
  { label: "PIP Feedback", paramName: "PipFeedbackFilter" },
  { label: "Patient Safety", paramName: "PatientSafetyFilter" }
];

export default function ResultFilters(props: IProps) {
  const specialties = sortPrimaryCertifications(props.certifications.filter(_ => _.isPrimary));
  const subspecialties = sortCertificationsAlphabetically(props.certifications.filter(_ => !_.isPrimary));

  const updateBooleanFilter = (paramName: string, checked: boolean) => {
    const next = new URLSearchParams(props.searchParams);
    if (checked) next.set(paramName, "true");
    else next.delete(paramName);
    next.set("PageNumber", "1");
    props.setSearchParams(next);
  };

  const updateDateFilter = (paramName: string, value: string) => {
    const next = new URLSearchParams(props.searchParams);
    if (value) next.set(paramName, value);
    else next.delete(paramName);
    next.set("PageNumber", "1");
    props.setSearchParams(next);
  };

  const updateDateOperator = (prefix: string, value: string) => {
    const next = new URLSearchParams(props.searchParams);
    next.set(`${prefix}Operator`, value);
    if (value !== "between") next.delete(`${prefix}EndValue`);
    next.set("PageNumber", "1");
    props.setSearchParams(next);
  };

  return (
    <aside className="aa-result-filters" aria-label="Result filters">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h5 text-secondary fw-bold mb-0">Filters</h2>
        <Button variant="outline-secondary" size="sm" onClick={props.onClearFilters} disabled={props.clearDisabled}>
          Clear
        </Button>
      </div>

      <fieldset className="aa-filter-group">
        <legend>Certification</legend>
        <CertificationCheckboxes
          title="Primary Certifications"
          certifications={specialties}
          selectedCertificationIds={props.selectedCertificationIds}
          onCertificationChange={props.onCertificationChange}
        />
        <CertificationCheckboxes
          title="Subspecialties"
          certifications={subspecialties}
          selectedCertificationIds={props.selectedCertificationIds}
          onCertificationChange={props.onCertificationChange}
        />
        <Form.Check
          id="IncludeAllCertificationsActivities"
          className="mt-2"
          type="checkbox"
          label="Applies to all sub/specialties"
          checked={props.includeAll}
          onChange={e => props.onIncludeAllChange(e.target.checked)}
        />
      </fieldset>

      <fieldset className="aa-filter-group">
        <legend>Activity Type</legend>
        {activityTypeOptions.map(option =>
          <Form.Check
            key={option.paramName}
            id={option.paramName}
            type="checkbox"
            label={option.label}
            checked={props.searchParams.get(option.paramName) === "true"}
            onChange={e => updateBooleanFilter(option.paramName, e.target.checked)}
          />
        )}
      </fieldset>

      <DateFilterGroup
        title="Event Begin"
        prefix="EventStartDate"
        searchParams={props.searchParams}
        onOperatorChange={updateDateOperator}
        onValueChange={updateDateFilter}
      />

      <DateFilterGroup
        title="Event End"
        prefix="EventEndDate"
        searchParams={props.searchParams}
        onOperatorChange={updateDateOperator}
        onValueChange={updateDateFilter}
      />
    </aside>
  );
}

function CertificationCheckboxes(props: {
  title: string;
  certifications: Certification[];
  selectedCertificationIds: number[];
  onCertificationChange: (certificationIds: number[]) => void;
}) {
  const updateSelection = (certificationId: number, checked: boolean) => {
    const selectedIds = checked
      ? [...props.selectedCertificationIds, certificationId]
      : props.selectedCertificationIds.filter(_ => _ !== certificationId);

    props.onCertificationChange(Array.from(new Set(selectedIds)));
  };

  return (
    <div className="aa-certification-filter-group">
      <div className="aa-filter-subtitle">{props.title}</div>
      {props.certifications.map(certification =>
        <Form.Check
          key={certification.id}
          id={`CertificationIds-${certification.id}`}
          type="checkbox"
          label={certification.name}
          checked={props.selectedCertificationIds.includes(certification.id)}
          onChange={e => updateSelection(certification.id, e.target.checked)}
        />
      )}
    </div>
  );
}

function DateFilterGroup(props: {
  title: string;
  prefix: string;
  searchParams: URLSearchParams;
  onOperatorChange: (prefix: string, value: string) => void;
  onValueChange: (paramName: string, value: string) => void;
}) {
  const operator = props.searchParams.get(`${props.prefix}Operator`) ?? "after";
  const betweenSelected = operator === "between";

  return (
    <fieldset className="aa-filter-group">
      <legend>{props.title}</legend>
      <Form.Select
        size="sm"
        className="mb-2"
        aria-label={`${props.title} filter type`}
        value={operator}
        onChange={e => props.onOperatorChange(props.prefix, e.target.value)}>
        <option value="after">All after</option>
        <option value="before">All before</option>
        <option value="on">On</option>
        <option value="between">Range</option>
      </Form.Select>
      <Form.Control
        size="sm"
        type="date"
        aria-label={`${props.title} filter date`}
        value={props.searchParams.get(`${props.prefix}Value`) ?? ""}
        onChange={e => props.onValueChange(`${props.prefix}Value`, e.target.value)}
      />
      {betweenSelected &&
        <Form.Control
          size="sm"
          className="mt-2"
          type="date"
          aria-label={`${props.title} filter end date`}
          value={props.searchParams.get(`${props.prefix}EndValue`) ?? ""}
          onChange={e => props.onValueChange(`${props.prefix}EndValue`, e.target.value)}
        />
      }
    </fieldset>
  );
}

function sortPrimaryCertifications(certifications: Certification[]) {
  return [...certifications].sort((a, b) =>
    (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
    || a.name.localeCompare(b.name)
  );
}

function sortCertificationsAlphabetically(certifications: Certification[]) {
  return [...certifications].sort((a, b) => a.name.localeCompare(b.name));
}
